import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, CalendarPlus, BookOpen, ClipboardCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { hasAcademyAccess } from "@/lib/academyAccess";
import { money, editionLabel, EDITION_STATUS, btnBrand } from "@/lib/academyFormat";
import CourseModal from "@/components/academy/CourseModal";
import EditionModal from "@/components/academy/EditionModal";
import AttendanceModal from "@/components/academy/AttendanceModal";
import { ACADEMY } from "@/constants/testIds";

function RatesTab({ courses, instructors, canEdit }) {
  const { toast } = useToast();
  const [rates, setRates] = useState({});
  const key = (i, c) => `${i}|${c}`;
  useEffect(() => {
    api.get("/academy/instructor-rates").then(({ data }) => {
      setRates(Object.fromEntries(data.map((r) => [key(r.instructor_id, r.course_id), String(r.percent)])));
    }).catch(() => {});
  }, []);
  const save = async (i, c) => {
    const v = rates[key(i, c)];
    if (v === "" || v == null) return;
    try {
      await api.put("/academy/instructor-rates", { instructor_id: i, course_id: c, percent: Number(v) });
      toast({ title: "Porcentaje guardado" });
    } catch (err) {
      toast({ title: "Error", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };
  if (instructors.length === 0) return <p className="text-sm text-muted-foreground">Nadie tiene el permiso de instructor (Equipo → permisos en la academia).</p>;
  return (
    <div className="bg-white border border-border rounded-xl overflow-x-auto">
      <table className="w-full text-sm" data-testid={ACADEMY.ratesTable}>
        <thead>
          <tr className="text-xs uppercase tracking-[0.1em] text-muted-foreground bg-[#E9F3F3]">
            <th className="text-left font-medium py-3 px-4">Instructor</th>
            {courses.map((c) => <th key={c.id} className="text-left font-medium py-3 px-3">{c.name}</th>)}
          </tr>
        </thead>
        <tbody>
          {instructors.map((i) => (
            <tr key={i.id} className="border-t border-border">
              <td className="py-2 px-4 font-medium">{i.name}</td>
              {courses.map((c) => (
                <td key={c.id} className="py-2 px-3">
                  <div className="flex items-center gap-1">
                    <Input
                      type="number" min="0" max="100" step="0.5" className="w-20 h-8" disabled={!canEdit}
                      value={rates[key(i.id, c.id)] ?? ""} onChange={(e) => setRates((r) => ({ ...r, [key(i.id, c.id)]: e.target.value }))}
                      onBlur={() => canEdit && save(i.id, c.id)} data-testid={`${ACADEMY.rateInputPrefix}${i.id}-${c.id}`}
                    />
                    <span className="text-muted-foreground">%</span>
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Fix U (2026-10-13): las certificaciones de la academia y sus fechas.
export default function AcademyCoursesPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const canEdit = hasAcademyAccess(user, ["academic"]);
  const canRates = hasAcademyAccess(user, ["finance"]);
  const [courses, setCourses] = useState([]);
  const [editions, setEditions] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [reservation, setReservation] = useState(200);
  const [courseModal, setCourseModal] = useState({ open: false, course: null });
  const [editionModal, setEditionModal] = useState({ open: false, course: null, edition: null });
  // Fix Y (2026-10-13): asistencia de cada fecha (instructor o area academica)
  // Fix D (2026-10-18): y recepcion
  const canTeach = hasAcademyAccess(user, ["academic", "instructor", "reception"]);
  const [attendance, setAttendance] = useState({ open: false, course: null, edition: null });
  const [showPast, setShowPast] = useState(false);
  const [loadingCatalog, setLoadingCatalog] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, e, s, st] = await Promise.all([
        api.get("/academy/courses", { params: { include_inactive: true } }), api.get("/academy/editions"),
        api.get("/academy/settings"), api.get("/academy/staff"),
      ]);
      setCourses(c.data);
      setEditions(e.data);
      setReservation(s.data.reservation_amount);
      setInstructors(st.data.filter((x) => x.academy_roles.includes("instructor")));
    } catch (err) {
      toast({ title: "Error al cargar cursos", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  // Fix J (2026-10-27): carga los 15 cursos del catalogo 2026 (si se repite no duplica ni cambia precios)
  const loadCatalog = async () => {
    setLoadingCatalog(true);
    try {
      const { data } = await api.post("/academy/courses/load-catalog-2026");
      toast({ title: "Catálogo 2026 cargado", description: `${data.created} cursos nuevos${data.updated ? ` y ${data.updated} completados` : ""}.` });
      await load();
    } catch (err) {
      toast({ title: "No se pudo cargar el catálogo", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    } finally {
      setLoadingCatalog(false);
    }
  };
  // el boton sigue mientras falten cursos del catalogo, para que una carga cortada a la mitad se pueda completar
  const catalogLoaded = courses.filter((c) => c.module).length;

  const today = new Date().toISOString().slice(0, 10);
  const editionsOf = (cid) => editions.filter((e) => e.course_id === cid && (showPast || (e.end_date || e.start_date) >= today))
    .sort((a, b) => a.start_date.localeCompare(b.start_date));

  return (
    <div className="space-y-6" data-testid={ACADEMY.coursesPage}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-foreground">Cursos y fechas</h1>
          <p className="text-base text-muted-foreground mt-1">Las certificaciones, cada fecha con su cupo, instructor, lugar y precio de la semana.</p>
        </div>
        {canEdit && (
          <div className="flex flex-wrap gap-2">
            {catalogLoaded < 15 && (
              <Button variant="outline" onClick={loadCatalog} disabled={loadingCatalog} data-testid={ACADEMY.coursesLoadCatalogButton}>{loadingCatalog ? "Cargando..." : catalogLoaded > 0 ? "Completar catálogo 2026" : "Cargar catálogo 2026 (15 cursos)"}</Button>
            )}
            <Button className={btnBrand} onClick={() => setCourseModal({ open: true, course: null })} data-testid={ACADEMY.coursesAddButton}><Plus className="w-4 h-4" /> Nuevo curso</Button>
          </div>
        )}
      </div>

      <Tabs defaultValue="courses">
        <TabsList>
          <TabsTrigger value="courses">Cursos y fechas</TabsTrigger>
          {canRates && <TabsTrigger value="rates" data-testid={ACADEMY.ratesTab}>Porcentaje de instructores</TabsTrigger>}
        </TabsList>
        <TabsContent value="courses" className="pt-4 space-y-4">
          <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
            <input type="checkbox" checked={showPast} onChange={(e) => setShowPast(e.target.checked)} /> Ver también fechas pasadas
          </label>
          {courses.length === 0 && (
            <div className="bg-white border border-border rounded-xl p-12 text-center" data-testid={ACADEMY.coursesEmpty}>
              <BookOpen className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">Todavía no hay cursos cargados.</p>
            </div>
          )}
          {courses.map((c) => (
            <div key={c.id} className={`bg-white border border-border rounded-xl p-5 space-y-3 ${c.is_active ? "" : "opacity-60"}`} data-testid={`${ACADEMY.courseCardPrefix}${c.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-serif text-xl text-foreground">{c.name}{!c.is_active && <span className="text-xs text-muted-foreground ml-2">(inactivo)</span>}</p>
                  <p className="text-sm text-muted-foreground">
                    {c.module ? `${c.module} · ` : ""}Precio normal {money(c.base_price)} · reserva {money(c.reservation_amount_effective)} · {c.practices_required} prácticas
                  </p>
                </div>
                {canEdit && (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setCourseModal({ open: true, course: c })} data-testid={`${ACADEMY.courseEditButtonPrefix}${c.id}`}><Pencil className="w-4 h-4" /> Editar</Button>
                    <Button size="sm" className={btnBrand} onClick={() => setEditionModal({ open: true, course: c, edition: null })} data-testid={`${ACADEMY.editionAddButtonPrefix}${c.id}`}><CalendarPlus className="w-4 h-4" /> Nueva fecha</Button>
                  </div>
                )}
              </div>
              {editionsOf(c.id).length === 0 ? (
                <p className="text-sm text-muted-foreground">Sin fechas{showPast ? "" : " próximas"}.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs uppercase tracking-[0.1em] text-muted-foreground border-b border-border">
                        <th className="text-left font-medium py-2 pr-3">Fecha</th>
                        <th className="text-left font-medium py-2 px-3 hidden md:table-cell">Lugar</th>
                        <th className="text-left font-medium py-2 px-3">Lugares</th>
                        <th className="text-left font-medium py-2 px-3">Precio hoy</th>
                        <th className="text-left font-medium py-2 px-3 hidden lg:table-cell">Instructores</th>
                        <th className="text-left font-medium py-2 px-3">Estado</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {editionsOf(c.id).map((e) => (
                        <tr key={e.id} className="border-b border-border last:border-0" data-testid={`${ACADEMY.editionRowPrefix}${e.id}`}>
                          <td className="py-2 pr-3">{editionLabel(e)}</td>
                          <td className="py-2 px-3 hidden md:table-cell text-muted-foreground">{e.location || "—"}</td>
                          <td className="py-2 px-3">{e.capacity ? `${e.seats_taken} de ${e.capacity}` : `${e.seats_taken}`}</td>
                          <td className="py-2 px-3">{money(e.price_today.price)} <span className="text-xs text-muted-foreground">{e.price_today.label}</span></td>
                          <td className="py-2 px-3 hidden lg:table-cell text-muted-foreground">{e.instructors.map((i) => i.name).join(", ") || "—"}</td>
                          <td className="py-2 px-3"><Badge variant="outline" className="font-normal">{EDITION_STATUS[e.status] || e.status}</Badge></td>
                          <td className="py-2 pl-3 text-right">
                            {canTeach && <Button variant="ghost" size="icon" title="Asistencia" onClick={() => setAttendance({ open: true, course: c, edition: e })} data-testid={`${ACADEMY.editionAttendanceButtonPrefix}${e.id}`}><ClipboardCheck className="w-4 h-4" /></Button>}
                            {canEdit && <Button variant="ghost" size="icon" onClick={() => setEditionModal({ open: true, course: c, edition: e })} data-testid={`${ACADEMY.editionEditButtonPrefix}${e.id}`}><Pencil className="w-4 h-4" /></Button>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </TabsContent>
        {canRates && (
          <TabsContent value="rates" className="pt-4">
            <RatesTab courses={courses.filter((c) => c.is_active)} instructors={instructors} canEdit={canRates} />
          </TabsContent>
        )}
      </Tabs>

      <CourseModal
        open={courseModal.open} onOpenChange={(o) => setCourseModal((m) => ({ ...m, open: o }))} course={courseModal.course}
        defaultReservation={reservation} onSaved={() => { toast({ title: "Curso guardado" }); load(); }}
      />
      <AttendanceModal open={attendance.open} onOpenChange={(o) => setAttendance((a) => ({ ...a, open: o }))} edition={attendance.edition} course={attendance.course} />
      <EditionModal
        open={editionModal.open} onOpenChange={(o) => setEditionModal((m) => ({ ...m, open: o }))} course={editionModal.course}
        edition={editionModal.edition} instructors={instructors} onSaved={() => { toast({ title: "Fecha guardada" }); load(); }}
      />
    </div>
  );
}
